// SỐ LIỆU KÊNH (07/10/2026, nhóm 3 thao tác xây kênh) — phần THUẦN dùng chung plugin (runner gom số liệu), Boss (chuẩn hoá + lưu chuỗi thời gian) và trang web
// (bảng tăng trưởng + đường nhỏ trên hộp "Số liệu kênh" của thẻ bot).
// Khối so_lieu_kenh (src/shared/khoi-so-lieu.ts) CHỈ ĐỌC trang của chính mình (không bấm gì rủi ro); bước cuối mang `bao_so_lieu` → runner gom các biến đã đọc thành
// RunRecord.soLieuKenh; Boss nhận ở run.final (server/src/so-lieu-kenh.ts).

export const TRUONG_SO_LIEU = [
  { ma: "nguoi_theo_doi", nhan: "Người theo dõi / đăng ký" },
  { ma: "dang_theo_doi", nhan: "Đang theo dõi" },
  { ma: "so_bai", nhan: "Số bài / video" },
  { ma: "luot_xem", nhan: "Lượt xem (bài / video gần đây)" },
  { ma: "luot_thich", nhan: "Lượt thích" },
  { ma: "karma", nhan: "Karma (Reddit)" },
] as const;
export type TruongSoLieu = (typeof TRUONG_SO_LIEU)[number]["ma"];
const LA_TRUONG = new Set<string>(TRUONG_SO_LIEU.map((t) => t.ma));
export const MANG_SO_LIEU = ["facebook", "x", "tiktok", "youtube", "reddit"] as const;
export type MangSoLieu = (typeof MANG_SO_LIEU)[number];

/** Phần đã học của bước báo số liệu (BundleStep.bao_so_lieu): tên BIẾN của lần chạy cho từng chỉ số. tong = cộng một trường của danh sách (vd lượt xem các video gần đây). */
export interface CauHinhBaoSoLieu {
  nen_tang: string;
  tai_khoan?: string;
  chi_so?: Partial<Record<TruongSoLieu, string>>;
  tong?: Partial<Record<TruongSoLieu, { ds: string; truong: string; toi_da?: number }>>;
}
export interface SoLieuKenh {
  nenTang: string;
  taiKhoan: string;
  luc: string; // ISO
  chiSo: Partial<Record<TruongSoLieu, number>>;
  soMuc?: Partial<Record<TruongSoLieu, number>>; // chỉ số kiểu "tong": cộng bao nhiêu mục
}

export const SO_TOI_DA = 1e12;
const DON_VI: [RegExp, number][] = [[/^(nghìn|ngàn|k|n)$/i, 1e3], [/^(triệu|tr|m)$/i, 1e6], [/^(tỷ|tỉ|b)$/i, 1e9]];

/** Chữ số đếm trên trang ("125 nghìn người đăng ký", "Có 2,9K người theo dõi", "1,1 N video", "1.234", "4.8M") → số nguyên ≥ 0; không có số → null. */
export function docSoDem(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) && v >= 0 ? Math.round(v) : null;
  if (typeof v !== "string") return null;
  const m = /(\d+(?:[.,]\d{3})*(?:[.,]\d+)?)\s*(nghìn|ngàn|triệu|tỷ|tỉ|tr|k|n|m|b)?(?![\p{L}])/iu.exec(v.normalize("NFC"));
  if (!m) return null;
  const so = m[1]!;
  const dv = m[2] ? DON_VI.find(([re]) => re.test(m[2]!))?.[1] : undefined;
  let n: number;
  if (dv) {
    // Có đơn vị: dấu phẩy / chấm CUỐI là dấu thập phân ("1,5 triệu", "3.8K"); dấu khác là nhóm nghìn.
    const chuan = so.replace(/[.,](?=\d*[.,])/g, "").replace(",", ".");
    n = parseFloat(chuan) * dv;
  } else n = parseInt(so.replace(/[.,]/g, ""), 10);
  if (!Number.isFinite(n) || n < 0 || n > SO_TOI_DA) return null;
  return Math.round(n);
}

const catKyTu = (s: string, n: number): string => Array.from(s.replace(/\s+/g, " ").trim()).slice(0, n).join("");

/** "trang_sl.0.nguoi_theo_doi" → bien.trang_sl[0].nguoi_theo_doi (trang đọc thành danh sách MỘT mục: cả trang). Không có → undefined. */
function theoDuong(bien: Record<string, unknown>, duong: string): unknown {
  let v: unknown = bien;
  for (const p of duong.split(".")) {
    if (v === null || typeof v !== "object") return undefined;
    v = Array.isArray(v) ? (/^\d+$/.test(p) ? v[Number(p)] : undefined) : (v as Record<string, unknown>)[p];
  }
  return v;
}

/** Biến của lần chạy → bản ghi số liệu (chỉ số không đọc được thì KHÔNG có, không đoán 0). tong.toi_da: chỉ cộng N mục ĐẦU (mới nhất) — trang tải lười
 *  thêm mục khác nhau mỗi lần, cộng cố định N mục thì chuỗi thời gian mới so được. */
export function gomSoLieu(cfg: CauHinhBaoSoLieu, bien: Record<string, unknown>, luc: string): SoLieuKenh {
  const chiSo: Partial<Record<TruongSoLieu, number>> = {};
  const soMuc: Partial<Record<TruongSoLieu, number>> = {};
  for (const [t, ten] of Object.entries(cfg.chi_so ?? {}) as [TruongSoLieu, string][]) {
    if (!LA_TRUONG.has(t) || !ten) continue;
    const n = docSoDem(theoDuong(bien, ten));
    if (n !== null) chiSo[t] = n;
  }
  for (const [t, c] of Object.entries(cfg.tong ?? {}) as [TruongSoLieu, { ds: string; truong: string; toi_da?: number }][]) {
    if (!LA_TRUONG.has(t) || !c) continue;
    const goc = theoDuong(bien, c.ds);
    if (!Array.isArray(goc)) continue;
    const ds = c.toi_da && c.toi_da > 0 ? goc.slice(0, c.toi_da) : goc;
    let tong = 0, dem = 0;
    for (const x of ds) {
      const n = x && typeof x === "object" ? docSoDem((x as Record<string, unknown>)[c.truong]) : null;
      if (n !== null) { tong += n; dem++; }
    }
    if (dem) { chiSo[t] = tong; soMuc[t] = dem; }
  }
  const tk = cfg.tai_khoan ? theoDuong(bien, cfg.tai_khoan) : "";
  return { nenTang: cfg.nen_tang, taiKhoan: catKyTu(typeof tk === "string" ? tk : tk == null ? "" : String(tk), 100), luc, chiSo, ...(Object.keys(soMuc).length ? { soMuc } : {}) };
}

/** Boss KHÔNG tin dữ liệu bot gửi: mạng phải đúng, thời điểm hợp lệ, chỉ số đã biết là số hữu hạn 0..1e12 (làm tròn); không còn chỉ số nào → null. */
export function chuanHoaSoLieu(x: unknown): SoLieuKenh | null {
  if (!x || typeof x !== "object" || Array.isArray(x)) return null;
  const o = x as Record<string, unknown>;
  if (typeof o.nenTang !== "string" || !(MANG_SO_LIEU as readonly string[]).includes(o.nenTang)) return null;
  if (typeof o.luc !== "string" || !Number.isFinite(Date.parse(o.luc))) return null;
  const chiSo: Partial<Record<TruongSoLieu, number>> = {};
  const cs = o.chiSo && typeof o.chiSo === "object" && !Array.isArray(o.chiSo) ? (o.chiSo as Record<string, unknown>) : {};
  for (const [k, v] of Object.entries(cs)) if (LA_TRUONG.has(k) && typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= SO_TOI_DA) chiSo[k as TruongSoLieu] = Math.round(v);
  if (!Object.keys(chiSo).length) return null;
  const soMuc: Partial<Record<TruongSoLieu, number>> = {};
  const sm = o.soMuc && typeof o.soMuc === "object" && !Array.isArray(o.soMuc) ? (o.soMuc as Record<string, unknown>) : {};
  for (const [k, v] of Object.entries(sm)) if (LA_TRUONG.has(k) && typeof v === "number" && Number.isInteger(v) && v > 0 && v <= 10_000) soMuc[k as TruongSoLieu] = v;
  return { nenTang: o.nenTang, taiKhoan: catKyTu(typeof o.taiKhoan === "string" ? o.taiKhoan : "", 100), luc: new Date(o.luc).toISOString(), chiSo, ...(Object.keys(soMuc).length ? { soMuc } : {}) };
}

export interface DongTangTruong { truong: TruongSoLieu; nhan: string; moiNhat: number; dauKy: number; thayDoi: number | null; phanTram: number | null; soDiem: number; diem: number[] }
/** Bảng tăng trưởng: mỗi chỉ số có dữ liệu → mới nhất, đầu kỳ (điểm cũ nhất trong danh sách), thay đổi, % (đầu kỳ 0 → null). Thứ tự chỉ số cố định. */
export function bangTangTruong(ds: { luc: string; chiSo: Partial<Record<TruongSoLieu, number>> }[]): DongTangTruong[] {
  const sx = [...ds].sort((a, b) => Date.parse(a.luc) - Date.parse(b.luc));
  const out: DongTangTruong[] = [];
  for (const t of TRUONG_SO_LIEU) {
    const diem = sx.map((d) => d.chiSo[t.ma]).filter((v): v is number => typeof v === "number" && Number.isFinite(v));
    if (!diem.length) continue;
    const dauKy = diem[0]!, moiNhat = diem[diem.length - 1]!;
    const nhieu = diem.length > 1;
    out.push({ truong: t.ma, nhan: t.nhan, moiNhat, dauKy, thayDoi: nhieu ? moiNhat - dauKy : null, phanTram: nhieu && dauKy > 0 ? Math.round(((moiNhat - dauKy) / dauKy) * 1000) / 10 : null, soDiem: diem.length, diem });
  }
  return out;
}

/** Toạ độ "x,y x,y …" cho <polyline> của đường nhỏ trong khung rộng × cao (giá trị lớn ở trên). < 2 điểm → "". */
export function duongNho(gt: number[], rong: number, cao: number): string {
  if (gt.length < 2) return "";
  const min = Math.min(...gt), max = Math.max(...gt);
  const r = (v: number) => String(Math.round(v * 10) / 10);
  return gt.map((v, i) => `${r((i * rong) / (gt.length - 1))},${r(max === min ? cao / 2 : cao - ((v - min) / (max - min)) * cao)}`).join(" ");
}
